/**
 * Device.js is the interface with the Native Application
 * deployed via Cordova.
 * 
 * It sets unique device configurations
 * and allows us to use Native features like bio-metrics, camera,
 * and contacts
 */
import router from '@/router/router'
import { store } from '@/store'
import Analytics from '@/services/Analytics'

export default {
    //  Handles the ApplicationLoaded event
    onApplicationLoaded() {
        store.getters.appDebug && console.log('DEVICE: onApplicationLoaded')

        //  Set device attributes in the vuex store
        store.commit('browserSet')
        store.commit('osSet')

        //  Check if we're on an iOS device
        const ios = store.getters.isMobile('ios')

        //  If ios, attach this so our screen will scroll back down and not give weird padding issues
        if (ios) document.addEventListener('focusout', function(e) {window.scrollTo(0, 0)});

        /**
         * 'deviceready' is an event unique to Cordova.  It lets us know we
         * are running as a Native Mobile Application (iOS or Android)
         * 
         * We use this handler to set Android and iOS specific configurations
         */
        document.addEventListener('deviceready', function () {
            //  Set the 'isNativeApp' attribute (tells the app we're running the Mobile App)
            store.commit('isNativeAppSet', true)

            //  We want to LOCK our screen to portrait only
            screen.orientation.lock('portrait')

            //  If we are NOT on iOS, set Android status bar
            if (!ios) return StatusBar.styleLightContent()

            //  Else, set iOS status bar
            StatusBar.styleDefault();

            //  For iOS, we allow overflow when doing sign up and make sure we have full sized window
            document.documentElement.style.setProperty('overflow', 'auto')
            document.getElementsByClassName('v-application--wrap')[0].classList.remove('mobile--wrap')

            //  Define the 'success' callback for iOS Keychain
            const success = async (val) => {
                //  If the keychain token does NOT exist, do nothing
                if (!val) return

                //  Else, if a user is already signed in, do nothing
                if (store.getters.user) return

                //  Sign em in
                const auth = await store.dispatch('authUserGet', val)

                //  If login failed, do nothing
                if (!auth.user) return

                //  Else, navigate to memberships
                router.push('/memberships')
            }

            //  Define the 'fail' callback for IOS Keychain
            const fail = () => { console.error('DEVICE: KEYCHAIN GET FAILURE ') }

            //  Attempt to get our iOS device token
            Keychain.get(success, fail, 'token', 'TouchID Message')
        })
    },
    //  Handles the AuthTokenSet event
    onAuthTokenSet(token) {
        store.getters.appDebug && console.log('DEVICE: onAuthTokenSet')

        //  Check if this is a mobile app
        const isNativeApp = store.getters.isNativeApp

        //  Check if we're on an iOS device
        const isIOS = store.getters.isMobile('ios')

        //  Check if we use Keychain
        const useKeychain = isNativeApp && isIOS

        //  If we do NOT use Keychain, do nothing
        if (!useKeychain) return

        //  Else, if the token exists...
        if (token) {
            //  Define the 'success' callback for iOS Keychain
            const success = () => { console.log('DEVICE: Keychain set successful') }

            //  Define the 'fail' callback for iOS Keychain
            const fail = () => { console.error('DEVICE: Keychain set failure') }

            // @ts-ignore - Save token in keychain
            Keychain.set(success, fail, 'token', token, false)
        }

        //  Else, the token does NOT exist...
        else {
            //  Define the 'success' callback for iOS Keychain
            const success = () => { console.log('DEVICE: Keychain remove success') }

            //  Define the 'fail' callback for iOS Keychain
            const fail = () => { console.error('DEVICE: Keychain remove failure') }

            // @ts-ignore - Remove token from keychain 
            Keychain.remove(success, fail, 'token')
        }
    },
    //  Handles the TwoFactorTokenSet event
    onTwoFactorTokenSet(twoFactorToken) {
        store.getters.appDebug && console.log('DEVICE: onTwoFactorTokenSet')

        //  Check if this is a mobile app
        const isNativeApp = store.getters.isNativeApp

        //  Check if we're on an iOS device
        const isIOS = store.getters.isMobile('ios')

        //  Check if we use Keychain
        const useKeychain = isNativeApp && isIOS

        //  If we do NOT use Keychain, do nothing
        if (!useKeychain) return

        //  Else, if the token exists...
        if (twoFactorToken) {
            //  Define the 'success' callback for iOS Keychain
            const success = () => { console.log('DEVICE: Keychain set successful') }

            //  Define the 'fail' callback for iOS Keychain
            const fail = () => { console.error('DEVICE: Keychain set failure') }

            // @ts-ignore - Save token in keychain
            Keychain.set(success, fail, 'two_factor_token', twoFactorToken, false)
        }

        //  Else, the token does NOT exist...
        else {
            //  Define the 'success' callback for iOS Keychain
            const success = () => { console.log('DEVICE: Keychain remove success') }

            //  Define the 'fail' callback for iOS Keychain
            const fail = () => { console.error('DEVICE: Keychain remove failure') }

            // @ts-ignore - Remove token from keychain 
            Keychain.remove(success, fail, 'two_factor_token')
        }
    },
    //  Handles the TenantSet event
    onTenantSet(tenant) {
        console.log('DEVICE: onTenantSet')

        //  Check if we're in a native mobile app
        const isNativeApp = store.getters.isNativeApp

        //  If we're NOT in a native app, do nothing
        if (!isNativeApp) return

        //  Else, check if we're running iOS
        const isIOS = store.getters.isMobile('ios')

        //  Add a class for our mobile app to look nice for ios
        if (isIOS) {
            //  This changes our application wrap size so we can see bottom navbar
            let app = document.getElementsByClassName('v-application--wrap')
            app[0].classList.add('mobile--wrap')

            //  Makes sure our html tag has hidden overflow once we are signed in
            document.documentElement.style.setProperty('overflow', 'hidden')
        }

        //  Sets links to open external browser
        window.open = window.cordova.InAppBrowser.open

        //  If we fail, output the error
        const fail = (error) => { console.error(error) }

        //  Define the successful token retrieval behavior
        const success = (token) => {
            store.dispatch('deviceTokenPost', { token: token }).then(() => {
                store.getters.appDebug && console.log('Device Token Stored')
            })
        }

        //  Get our device token
        FirebasePlugin.onTokenRefresh(success, fail)

        //  Track with segment that a new mobile device has signed in
        Analytics.track('MobileDeviceSignIn', { device: store.getters.os })
    },
    //  Handles the UserLogin event
    onUserLogin(user) {
        store.getters.appDebug && console.log('DEVICE: onUserLogin')

        //  Check if we're on an iOS device
        const iOS = store.getters.isNativeApp && store.getters.isMobile('ios')

        //  If we're on an iOS device, we need to save the auth token in iOS Keychain
        if (iOS) {
            //  Save token in keychain
            const token = store.getters.token
            const setSuccess = (val) => { console.log('DEVICE: Keychain set successful') }
            const setFail = (val) => { console.error('DEVICE: Keychain set failure') }
            Keychain.set(setSuccess,setFail,'token',token,false)
        }

        //  Add the Cordova 'deviceready' listener
        document.addEventListener('deviceready', function () {

            //  Tells our store that we are on an app device, MUST BE MOBILE
            store.commit('appDeviceSet', true)

            //  Define the successful notification tap behavior
            const msgreceived = async (message) => {

                //  If message tap does NOT exist, do nothing
                if (!message.tap) return

                //  Log this notification
                store.getters.appDebug && console.log('DEVICE: Push Notification Received', message)

                //  Check if the message is for the authenticated user
                const isUser = store.getters.user.id == message.user_id

                //  If the message is NOT for the authenticated user, do nothing
                if (!isUser) return

                //  Fetch the tenant
                const tenant = await store.dispatch('tenantGet', message.tenant_id)

                //  Our inbox route will contain the channel id from the message
                const route = message.route

                //  Push to the route
                router.push(route)

                //  Reset the tenant key so we make sure we are in a fresh state
                // store.commit('tenantKeySet', `${tenant.subdomain}Key`)
            }

            //  If we fail, output the error
            const fail = () => { console.error(error) }

            //  Handling for receiving a push notification
            FirebasePlugin.onMessageReceived(msgreceived, fail);
        })
    },
    //  Handles the UserLogout event
    onUserLogout() {
        store.getters.appDebug && console.log('DEVICE: onUserLogout')

        //  Check if we're on an iOS device
        const iOS = store.getters.isNativeApp && store.getters.isMobile('ios')

        //  If we're on an iOS device, we need to remove Keychain values
        if (iOS) {
            const remSuccess = (val) => { console.log('DEVICE: Keychain remove success', val) }
            const remFail = (val) => { console.log('DEVICE: Keychain remove failure', val) }
            Keychain.remove(remSuccess, remFail, 'token')
        }

        //  Remove device listeners (ie restore everything once we logout)
        document.addEventListener('deviceready', function () {
            //  If we are on the ios app, we want to allow overflow when doing sign up and make sure we have full sized window
            if (store.getters.isMobile('ios')) {
                document.documentElement.style.setProperty('overflow', 'auto')
                document.getElementsByClassName('v-application--wrap')[0].classList.remove('mobile--wrap')
            }
        })
    },
    //  Import contacts store on the Native device
    importContacts() {

        //  Start a loading spinner so the user knows what is happening
        store.commit('splashSet', { message: 'Importing device contacts. This may take some time, please be patient.' })

        //  Define the error callback
        const onError = (error) => { alert('onError!') }

        //  Define the success callback
        const onSuccess = async (contacts) => {

            //  Send this whole payload to the API so they can process it
            await store.dispatch('personsPostBatch', { contacts: contacts, token: store.getters.deviceToken })

            //  Import & Upload are complete, so remove the splash screen
            store.commit('splashScreenSet', null)
        }

        //  Import contacts from the mobile device
        navigator.contacts.find(['*'], onSuccess, onError, { multiple: true })
    }
}
